import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { supabase } from "../integrations/supabase/client";
import { useToast } from "../hooks/use-toast";
import { Loader2, Send, X } from "lucide-react";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const feedbackTypes = [
  { value: "bug_report", label: "Bug Report", icon: "🐛", color: "bg-red-100 text-red-800 border-red-200" },
  { value: "feature_request", label: "Feature Request", icon: "💡", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "improvement", label: "Improvement", icon: "⚡", color: "bg-green-100 text-green-800 border-green-200" },
  { value: "general", label: "General", icon: "💬", color: "bg-gray-100 text-gray-800 border-gray-200" },
];

const priorities = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-700 border-gray-200" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { value: "critical", label: "Critical", color: "bg-red-100 text-red-700 border-red-200" },
];

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [feedbackType, setFeedbackType] = useState("general");
  const [priority, setPriority] = useState("medium");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in both title and description.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { error } = await supabase
        .from("feedback")
        .insert({
          user_id: user.id,
          title: title.trim(),
          description: description.trim(),
          feedback_type: feedbackType,
          priority,
        });

      if (error) {
        throw error;
      }

      toast({
        title: "Feedback Submitted",
        description: "Thank you for your feedback! We'll review it and get back to you soon.",
      });

      // Reset form
      setTitle("");
      setDescription("");
      setFeedbackType("general");
      setPriority("medium");
      
      // Close modal
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] bg-background border border-border/50 shadow-2xl">
        <DialogHeader className="text-center pb-4">
          <DialogTitle className="text-2xl font-bold text-foreground">
            Share Your Feedback
          </DialogTitle>
          <p className="text-muted-foreground text-base mt-1">
            Help us improve your experience
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Feedback Type Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-foreground">What type of feedback do you have?</Label>
            <div className="grid grid-cols-2 gap-3">
              {feedbackTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFeedbackType(type.value)}
                  className={`p-4 rounded-xl border-2 ${
                    feedbackType === type.value
                      ? "border-primary bg-primary/10"
                      : "border-border/50 bg-background"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{type.icon}</span>
                    <div className="text-left">
                      <div className="font-semibold text-foreground text-sm">{type.label}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Priority Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-foreground">Priority Level</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="h-11 text-base border-2 border-border/50 bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border-border/50">
                {priorities.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="h-11">
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className={`${p.color} border`}>
                        {p.label}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title Input */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-base font-semibold text-foreground">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              maxLength={100}
              className="h-11 text-base border-2 border-border/50 bg-background focus:border-primary"
            />
            <div className="text-sm text-muted-foreground text-right">
              {title.length}/100
            </div>
          </div>

          {/* Description Input */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-base font-semibold text-foreground">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide detailed information about your feedback..."
              rows={4}
              maxLength={1000}
              className="text-base border-2 border-border/50 bg-background focus:border-primary resize-none"
            />
            <div className="text-sm text-muted-foreground text-right">
              {description.length}/1000
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="h-11 px-6 rounded-full border-2 border-border/50"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="h-11 px-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Submit Feedback
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
